import React, { ReactNode } from 'react';
import { usePageOwner, useShareUrls } from '../../hooks';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { useTranslation } from 'react-i18next';
import { filterByTypename } from '../../utils/graphql';
import { ShareContextProvider } from '../../context/ShareContext';

interface FundraisingShareProps {
  children: ReactNode;
}

export function FundraisingShareProvider({ children }: FundraisingShareProps) {
  const { isPageOwner } = usePageOwner();
  const { data: basePageData } = useBasePageQuery();
  const { t } = useTranslation('common', { useSuspense: false });

  const charities = filterByTypename(
    basePageData?.page?.relationships?.beneficiaries?.nodes ?? [],
    'Charity',
  );

  const charityName = charities[0]?.name
    ? charities[0].name
    : t('charity.defaultName');

  const emailMapper = (url: string) => ({
    subject: t('common.defaultEmailSubject'),
    body: isPageOwner
      ? t('fundraising.shareEmailPageOwner', {
          charityName,
          emailUrl: url,
        })
      : t('fundraising.shareEmailNonPageOwner', {
          name: basePageData?.page?.owner?.name,
          charityName,
          emailUrl: url,
        }),
  });

  const whatsAppAndSmsMapper = (url: string) => {
    const whatsappAndSmsMessage = isPageOwner
      ? t('fundraising.shareWhatsappSMSPageOwner', {
          charityName,
        })
      : t('fundraising.shareWhatsappSMSNonPageOwner', {
          name: basePageData?.page?.owner?.name,
          charityName,
        });

    return `${whatsappAndSmsMessage} ${encodeURIComponent(url)}`;
  };

  const twitterMapper = () => {
    return isPageOwner
      ? t('fundraising.shareTwitterPageOwner', {
          charityName,
        })
      : t('fundraising.shareTwitterNonPageOwner', {
          name: basePageData?.page?.owner?.name,
          charityName,
        });
  };

  const shareItems = useShareUrls({
    email: emailMapper,
    sms: whatsAppAndSmsMapper,
    twitter: twitterMapper,
    whatsApp: whatsAppAndSmsMapper,
  });

  return (
    <ShareContextProvider value={{ shareItems }}>
      {children}
    </ShareContextProvider>
  );
}
